import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { testimonialService } from '../../services/testimonialService.js';
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle, CreditCard, MessageSquareQuote } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const EditTestimonial = () => {
  const { testimonialId } = useParams();
  const [formData, setFormData] = useState({
    full_name: '',
    location: '',
    rating: '5',
    testimonial_text: '',
    is_active: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageBase64, setExistingImageBase64] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonial();
  }, [testimonialId]);

  const fetchTestimonial = async () => {
    try {
      const response = await testimonialService.getTestimonial(testimonialId);
      const data = response.data.data;
      setFormData({
        full_name: data.full_name,
        location: data.location,
        rating: data.rating.toString(),
        testimonial_text: data.testimonial_text,
        is_active: data.is_active,
      });
      
      // Set existing image if available (base64 format)
      if (data.image_base64) {
        setExistingImageBase64(data.image_base64);
      }
    } catch (err) {
      Alert.error('Failed to load testimonial data');
      console.error(err);
      navigate('/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (1MB)
      if (file.size > 1048576) {
        Alert.error('Image size must be less than 1MB');
        e.target.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        Alert.error('Only PNG, JPG, JPEG, GIF, and WebP images are allowed');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setRemoveImage(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setExistingImageBase64(null);
    setRemoveImage(true);
    const imageInput = document.querySelector('input[name="image"]');
    if (imageInput) {
      imageInput.value = '';
    }
  };

  const getDisplayImage = () => {
    if (imagePreview) {
      return imagePreview;
    }
    if (existingImageBase64) {
      return `data:image/jpeg;base64,${existingImageBase64}`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const form = new FormData();
      form.append('full_name', formData.full_name);
      form.append('location', formData.location);
      form.append('rating', formData.rating);
      form.append('testimonial_text', formData.testimonial_text);
      form.append('is_active', formData.is_active);

      // Handle image
      if (e.target.image.files[0]) {
        form.append('image', e.target.image.files[0]);
      } else if (removeImage) {
        form.append('remove_image', 'true');
      }

      await testimonialService.updateTestimonial(testimonialId, form);
      Alert.success('Testimonial updated successfully!');
      setTimeout(() => navigate('/testimonials'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-3 sm:mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading testimonial data...</p>
            </div>
        </div>
      </DashboardLayout>
    );
  }

  const displayImage = getDisplayImage();

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <MessageSquareQuote className="w-6 h-6 text-buttonBg" /> Edit Testimonial
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                  {/* Customer Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Customer Information
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Full Name <span class="text-red-500 ml-1">*</span></label>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Location <span class="text-red-500 ml-1">*</span></label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Customer Image (Optional)</label>
                        
                        {/* Show existing or new image preview */}
                        {displayImage && !imageError && (
                          <div className="mb-3 sm:mb-4">
                            <div className="relative inline-block">
                              <img 
                                src={displayImage} 
                                alt="Preview" 
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-gray-200"
                                onError={() => setImageError(true)}
                              />
                              <button
                                type="button"
                                onClick={handleRemoveImage}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                title="Remove image"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 mt-2">
                              {imagePreview ? 'New image selected' : 'Current image'}
                            </p>
                          </div>
                        )}

                        <input
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <p className="text-xs sm:text-sm text-gray-600 mt-2">
                          Upload a new image to replace the existing one. Max size: 1MB. Formats: PNG, JPG, JPEG, GIF, WebP
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Testimonial Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Testimonial Details
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Rating <span class="text-red-500 ml-1">*</span></label>
                        <SelectField variant="form"
                          name="rating"
                          value={formData.rating}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value="5">5 ⭐⭐⭐⭐⭐</option>
                          <option value="4">4 ⭐⭐⭐⭐</option>
                          <option value="3">3 ⭐⭐⭐</option>
                          <option value="2">2 ⭐⭐</option>
                          <option value="1">1 ⭐</option>
                        </SelectField>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Testimonial Text <span class="text-red-500 ml-1">*</span></label>
                        <textarea
                          name="testimonial_text"
                          value={formData.testimonial_text}
                          onChange={handleChange}
                          rows={5}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Active Status <span class="text-red-500 ml-1">*</span></label>
                        <SelectField variant="form"
                          name="is_active"
                          value={formData.is_active}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value={true}>Active</option>
                          <option value={false}>Inactive</option>
                        </SelectField>
                        <p className="text-xs sm:text-sm text-gray-600 mt-2">Active testimonials will be displayed on the website</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button variant="primary" type="submit" loading={submitting}>
                      {submitting ? 'Updating...' : 'Update Testimonial'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/testimonials')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default EditTestimonial;